/**
 * Validation Functions
 *
 * This file contains all validation logic for Master Flight Records.
 * Includes both complete record validation and individual field validation.
 */

import type { MasterFlightRecord } from './models';
import { validateFrequencyPattern, getOperatingDates } from '../frequencyMatcher';

/** Convert a date string (YYYY-MM-DD) to its IATA day code (1=Mon … 7=Sun) */
export function getIataDayFromDate(dateStr: string): string {
  // Parse manually to avoid UTC-vs-local timezone shift from Date.parse
  // Handle ISO strings like "2026-03-15T00:00:00.000Z" — take only the date part
  const datePart = dateStr.slice(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);
  const jsDay = new Date(year, month - 1, day).getDay(); // 0=Sun, 1=Mon, …
  return String(jsDay === 0 ? 7 : jsDay);
}

/**
 * Validates all station sectors and returns an array of error messages.
 * Shared by validateRecord and validateRecordAll to eliminate duplication.
 */
function validateStationSectors(stations: MasterFlightRecord['stations']): string[] {
  const errors: string[] = [];

  if (!stations?.length) {
    errors.push('At least 1 station sector is required');
    return errors;
  }

  for (let i = 0; i < stations.length; i++) {
    const s = stations[i];
    if (!s.departureStation?.trim()) errors.push(`Sector ${i + 1}: departureStation is required`);
    if (!s.arrivalStation?.trim()) errors.push(`Sector ${i + 1}: arrivalStation is required`);
    if (s.departureStation?.trim() && s.arrivalStation?.trim() &&
        s.departureStation.trim().toUpperCase() === s.arrivalStation.trim().toUpperCase()) {
      errors.push(`Sector ${i + 1}: Departure and arrival cannot be the same airport`);
    }
    if (!s.std?.trim()) {
      errors.push(`Sector ${i + 1}: STD (departure time) is required`);
    } else if (!/^\d{4}$/.test(s.std) || parseInt(s.std.slice(0, 2), 10) > 23 || parseInt(s.std.slice(2, 4), 10) > 59) {
      errors.push(`Sector ${i + 1}: STD must be valid time (0000-2359)`);
    }
    if (!s.sta?.trim()) {
      errors.push(`Sector ${i + 1}: STA (arrival time) is required`);
    } else if (!/^\d{4}$/.test(s.sta) || parseInt(s.sta.slice(0, 2), 10) > 23 || parseInt(s.sta.slice(2, 4), 10) > 59) {
      errors.push(`Sector ${i + 1}: STA must be valid time (0000-2359)`);
    }
    if (s.utcLocalTimeVariationDep && !/^[+-][0-1][0-9][0-5][0-9]$/.test(s.utcLocalTimeVariationDep)) {
      errors.push(`Sector ${i + 1}: UTC Variation (Dep) must be in format ±hhmm (e.g., +0800, -0500)`);
    }
    if (s.utcLocalTimeVariationArr && !/^[+-][0-1][0-9][0-5][0-9]$/.test(s.utcLocalTimeVariationArr)) {
      errors.push(`Sector ${i + 1}: UTC Variation (Arr) must be in format ±hhmm (e.g., +0800, -0500)`);
    }
  }

  return errors;
}

/**
 * Validates a complete Master Flight Record
 * Returns error message if invalid, null if valid
 * This is the main validation function called before saving
 *
 * @param r - The flight record to validate
 * @returns Error message string or null if valid
 */
export function validateRecord(r: MasterFlightRecord): string | null {
  // Flight Owner validation - must be 2-3 uppercase alphanumeric characters
  const owner = r.flightOwner?.trim() || '';
  if (!owner) {
    return 'Flight Owner is required';
  }
  if (owner.length < 2 || owner.length > 3) {
    return 'Flight Owner must be 2-3 characters (IATA code)';
  }
  if (!/^[A-Z0-9]{2,3}$/.test(owner)) {
    return 'Flight Owner must be uppercase alphanumeric characters only (e.g., MH, OD, 5J)';
  }

  // Flight Number validation - must be 1-4 digits or airline code + digits
  const flightNo = r.flightNo?.trim() || '';
  if (!flightNo) {
    return 'Flight Number is required';
  }
  if (flightNo.length < 1 || flightNo.length > 10) {
    return 'Flight Number must be 1-10 characters';
  }
  if (!/^([A-Z0-9]{2,3}\s?)?\d{1,4}$/.test(flightNo)) {
    return 'Flight Number must be in format: "0017" or "MH 0017" (1-4 digits with optional airline code)';
  }

  // Operational Suffix validation - optional, max 1 character
  if (r.operationalSuffix && r.operationalSuffix.length > 1) {
    return 'Operational Suffix must be maximum 1 character';
  }

  // Service Type validation - required, exactly 1 character
  const serviceType = r.iataServiceTypeCode?.trim() || '';
  if (!serviceType) {
    return 'Service Type is required';
  }
  if (serviceType.length !== 1) {
    return 'Service Type must be exactly 1 character (e.g., J, P, H)';
  }

  // Variant ID validation - must be at least 1
  if (!r.itineraryVarId) {
    return 'Variant ID is required';
  }
  if (r.itineraryVarId < 1) {
    return 'Variant ID must be at least 1';
  }

  // Season ID validation - required, must be S## or W## format
  const seasonId = r.seasonId?.trim() || '';
  if (!seasonId) {
    return 'Season ID is required';
  }
  if (!/^[SW]\d{2}$/.test(seasonId)) {
    return 'Season ID must be in format S## or W## (e.g., S25, W24)';
  }

  // Date validations
  if (!r.startDate) return 'Start Date is required (format: YYYY-MM-DD)';
  if (!r.endDate) return 'End Date is required (format: YYYY-MM-DD)';

  // End date must be after start date
  if (new Date(r.endDate) < new Date(r.startDate)) {
    return 'End Date must be after Start Date';
  }

  // Frequency validation - use enhanced validation with duplicate detection
  const frequencyValidation = validateFrequencyPattern(r.frequency?.trim() || '');
  if (!frequencyValidation.isValid) {
    return frequencyValidation.error || 'Invalid frequency pattern';
  }

  // Zero operating dates check - catch frequency+date mismatch before API call
  if (r.startDate && r.endDate && r.frequency) {
    const dates = getOperatingDates(
      r.frequency,
      new Date(r.startDate),
      new Date(r.endDate),
      new Date(r.startDate),
      new Date(r.endDate)
    );
    if (dates.length === 0) {
      return 'Selected frequency produces 0 operating dates in this date range. Adjust frequency or dates.';
    }
  }

  // Boundary-day check: frequency must include the day-of-week of start and end dates
  // Skip for adhoc flights — they override a subset of the parent's schedule
  if (!r.isAdhoc && r.frequency && r.startDate && r.endDate) {
    const startDay = getIataDayFromDate(r.startDate);
    const endDay = getIataDayFromDate(r.endDate);
    const missingDays: string[] = [];
    if (!r.frequency.includes(startDay)) missingDays.push("start date's day");
    if (!r.frequency.includes(endDay)) missingDays.push("end date's day");
    if (missingDays.length > 0) {
      return `Frequency must include ${missingDays.join(' and ')}`;
    }
  }

  // Schedule Status validation
  if (!r.scheduleStatus?.trim()) {
    return 'Schedule Status is required';
  }
  if (!/^(ACTIVE|INACTIVE|CANCELLED|PENDING|SUSPENDED)$/i.test(r.scheduleStatus)) {
    return 'Schedule Status must be: ACTIVE, INACTIVE, CANCELLED, PENDING, or SUSPENDED';
  }

  // Stations validation
  const stationErrors = validateStationSectors(r.stations);
  if (stationErrors.length > 0) return stationErrors[0];

  return null;
}

/**
 * Validates a complete Master Flight Record and returns ALL errors.
 * Unlike validateRecord() which stops at the first error, this collects every issue.
 */
export function validateRecordAll(r: MasterFlightRecord): string[] {
  const errors: string[] = [];

  // === Priority order: Flight Owner → Flight Number → Service Type → Operating Days ===

  // 1. Flight Owner
  const owner = r.flightOwner?.trim() || '';
  if (!owner) {
    errors.push('Flight Owner is required');
  } else if (owner.length < 2 || owner.length > 3) {
    errors.push('Flight Owner must be 2-3 characters (IATA code)');
  } else if (!/^[A-Z0-9]{2,3}$/.test(owner)) {
    errors.push('Flight Owner must be uppercase alphanumeric characters only');
  }

  // 2. Flight Number
  const flightNo = r.flightNo?.trim() || '';
  if (!flightNo) {
    errors.push('Flight Number is required');
  } else if (!/^([A-Z0-9]{2,3}\s?)?\d{1,4}$/.test(flightNo)) {
    errors.push('Flight Number must be in format: "0017" or "MH 0017"');
  }

  // 3. Service Type
  const serviceType = r.iataServiceTypeCode?.trim() || '';
  if (!serviceType) {
    errors.push('Service Type is required');
  } else if (serviceType.length !== 1) {
    errors.push('Service Type must be exactly 1 character');
  }

  // 4. Operating Days / Frequency
  const freq = r.frequency?.trim() || '';
  const freqValidation = validateFrequencyPattern(freq);
  if (!freqValidation.isValid) {
    errors.push(freqValidation.error || 'Invalid frequency pattern');
  } else if (r.startDate && r.endDate && freq) {
    const dates = getOperatingDates(
      freq,
      new Date(r.startDate), new Date(r.endDate),
      new Date(r.startDate), new Date(r.endDate)
    );
    if (dates.length === 0) {
      errors.push('Selected frequency produces 0 operating dates in this date range');
    }

    // Boundary-day check (skip for adhoc flights)
    if (!r.isAdhoc) {
      const startDay = getIataDayFromDate(r.startDate);
      const endDay = getIataDayFromDate(r.endDate);
      const missingDays: string[] = [];
      if (!freq.includes(startDay)) missingDays.push("start date's day");
      if (!freq.includes(endDay)) missingDays.push("end date's day");
      if (missingDays.length > 0) {
        errors.push(`Frequency must include ${missingDays.join(' and ')}`);
      }
    }
  }

  // === Remaining fields ===

  // Operational Suffix
  if (r.operationalSuffix && r.operationalSuffix.length > 1) {
    errors.push('Operational Suffix must be maximum 1 character');
  }

  // Dates
  if (!r.startDate) errors.push('Start Date is required');
  if (!r.endDate) errors.push('End Date is required');
  if (r.startDate && r.endDate && new Date(r.endDate) < new Date(r.startDate)) {
    errors.push('End Date must be after Start Date');
  }

  // Season ID
  const seasonId = r.seasonId?.trim() || '';
  if (!seasonId) {
    errors.push('Season ID is required');
  } else if (!/^[SW]\d{2}$/.test(seasonId)) {
    errors.push('Season ID must be in format S## or W##');
  }

  // Schedule Status
  if (!r.scheduleStatus?.trim()) {
    errors.push('Schedule Status is required');
  }

  // Stations
  errors.push(...validateStationSectors(r.stations));

  return errors;
}

/**
 * Validates a specific field from MasterFlightRecord
 * Used for inline validation as user types
 *
 * @param fieldName - The field to validate
 * @param value - The field value
 * @param record - Optional full record for cross-field validation
 * @returns Error message or null if valid
 */
export function validateField(
  fieldName: keyof MasterFlightRecord,
  value: MasterFlightRecord[keyof MasterFlightRecord],
  record?: MasterFlightRecord
): string | null {
  switch (fieldName) {
    case 'flightOwner':
      const owner = (value as string)?.trim() || '';
      if (!owner) return 'Flight Owner is required';
      if (owner.length < 2 || owner.length > 3) return 'Must be 2-3 characters';
      if (!/^[A-Z0-9]{2,3}$/.test(owner)) return 'Must be uppercase alphanumeric only';
      return null;

    case 'flightNo':
      const flightNo = (value as string)?.trim() || '';
      if (!flightNo) return 'Flight Number is required';
      if (flightNo.length < 1 || flightNo.length > 10) return 'Must be 1-10 characters';
      if (!/^([A-Z0-9]{2,3}\s?)?\d{1,4}$/.test(flightNo)) {
        return 'Enter 1-4 digit number';
      }
      // Check if it has less than 1 or more than 4 digits
      const digitsPart = flightNo.split(/\s+/).pop() || '';
      if (digitsPart.length < 1 || digitsPart.length > 4) {
        return 'Number must be 1-4 digits';
      }
      return null;

    case 'operationalSuffix':
      if (value && (value as string).length > 1) return 'Maximum 1 character';
      return null;

    case 'iataServiceTypeCode':
      const serviceType = (value as string)?.trim() || '';
      if (!serviceType) return 'Service Type is required';
      if (serviceType.length !== 1) return 'Must be exactly 1 character';
      return null;

    case 'itineraryVarId':
      const varId = value as number;
      if (!varId) return 'Variant ID is required';
      if (varId < 1) return 'Must be at least 1';
      return null;

    case 'seasonId':
      const seasonId = (value as string)?.trim() || '';
      if (!seasonId) return 'Season ID is required';
      if (!/^[SW]\d{2}$/.test(seasonId)) return 'Format: S## or W## (e.g., S26)';
      return null;

    case 'startDate':
      if (!value) return 'Start Date is required';
      return null;

    case 'endDate':
      if (!value) return 'End Date is required';
      if (record?.startDate && new Date(value as string) < new Date(record.startDate)) {
        return 'Must be after Start Date';
      }
      return null;

    case 'frequency':
      const freq = (value as string)?.trim() || '';
      const freqValidation = validateFrequencyPattern(freq);
      if (!freqValidation.isValid) {
        return freqValidation.error || 'Invalid frequency';
      }
      if (freq && record?.startDate && record?.endDate) {
        const dates = getOperatingDates(
          freq,
          new Date(record.startDate),
          new Date(record.endDate),
          new Date(record.startDate),
          new Date(record.endDate)
        );
        if (dates.length === 0) {
          return '0 operating dates for this frequency + date range';
        }
        // Boundary-day check (skip for adhoc flights)
        if (!record?.isAdhoc) {
          const startDay = getIataDayFromDate(record.startDate);
          const endDay = getIataDayFromDate(record.endDate);
          const missingDays: string[] = [];
          if (!freq.includes(startDay)) missingDays.push("start date's day");
          if (!freq.includes(endDay)) missingDays.push("end date's day");
          if (missingDays.length > 0) {
            return `Frequency must include ${missingDays.join(' and ')}`;
          }
        }
      }
      return null;

    default:
      return null;
  }
}
